import { createContext, ReactNode, useCallback, useContext, useMemo } from 'react';

import { UsersService } from 'services';
import { CollaboratorRoleAccessType } from 'types';
import { SPMCollaborationRole } from 'types/SPM';

import { useSPMPlanContext } from 'contexts/SPMPlanContext';
import { useIsSpmCollaborationUser } from 'hooks/useIsSpmCollaborationUser';

export type SPMCollaborationContextType = {
  isCollaborationUser: boolean;
  collaborationRole: SPMCollaborationRole;
  getCollaborationRoleForModel: (modelId?: string) => SPMCollaborationRole;
  isScenarioViewer: boolean;
};

// A real default, not `{}`: consumers call getCollaborationRoleForModel, and components that render
// outside this provider would otherwise call undefined.
const SPMCollaborationContext = createContext<SPMCollaborationContextType>({
  isCollaborationUser: false,
  collaborationRole: null,
  getCollaborationRoleForModel: () => null,
  isScenarioViewer: false,
});
export const useSPMCollaborationContext = () => useContext(SPMCollaborationContext);

type Props = {
  children: ReactNode;
};

export const SPMCollaborationContextProvider = ({ children }: Props) => {
  const { query: permissionsQuery } = UsersService.useGetCurrentUserPermissions();
  const userPermissions = permissionsQuery.data;
  const isCollaborationUser = useIsSpmCollaborationUser(userPermissions);
  const { planningCycle, activeScenarioId } = useSPMPlanContext();
  const scenarioCollaborationRoles = planningCycle?.scenario_collaboration_roles;

  const collaborationRole: SPMCollaborationRole = useMemo(() => {
    if (!isCollaborationUser || !userPermissions) {
      return null;
    }
    return CollaboratorRoleAccessType.COLLABORATOR;
  }, [isCollaborationUser, userPermissions]);

  const getCollaborationRoleForModel = useCallback(
    (modelId?: string): SPMCollaborationRole => {
      if (!isCollaborationUser || !modelId || !activeScenarioId) {
        return collaborationRole;
      }
      const role = scenarioCollaborationRoles?.[`${activeScenarioId}:${modelId}`];
      return role === CollaboratorRoleAccessType.COMMENTER ? CollaboratorRoleAccessType.COMMENTER : collaborationRole;
    },
    [isCollaborationUser, activeScenarioId, scenarioCollaborationRoles, collaborationRole],
  );

  // Approvals belong to the scenario rather than to one model, so this asks whether the user is a
  // viewer of the scenario as a whole. Holding any other role on any model means they are not.
  const isScenarioViewer = useMemo(() => {
    if (!isCollaborationUser || !activeScenarioId) {
      return false;
    }
    const rolesInScenario = Object.entries(scenarioCollaborationRoles ?? {})
      .filter(([key]) => key.startsWith(`${activeScenarioId}:`))
      .map(([, role]) => role);
    return rolesInScenario.length > 0 && rolesInScenario.every((role) => role === CollaboratorRoleAccessType.COMMENTER);
  }, [isCollaborationUser, activeScenarioId, scenarioCollaborationRoles]);

  const value = useMemo(
    () => ({ isCollaborationUser, collaborationRole, getCollaborationRoleForModel, isScenarioViewer }),
    [isCollaborationUser, collaborationRole, getCollaborationRoleForModel, isScenarioViewer],
  );

  return <SPMCollaborationContext.Provider value={value}>{children}</SPMCollaborationContext.Provider>;
};
